<template>
    <FreelancerSignupStep
        data-testid="freelancer-signup-step-create-your-account"
        :save-step="handleSaveStep"
        :tracking-step-saved-action-context="trackingStepSavedActionContext"
    >
        <template #freelancer-signup-step-main>
            <FreelancerSignupMultiplePhases :is-visible-phase2="!displayEmailPhase">
                <template v-if="displayEmailPhase" #freelancer-signup-multiple-phase-1>
                    <VJoyWrapper direction="row" justify="space-between" wrap="wrap" align="stretch">
                        <VJoyWrapper
                            class="freelancer-signup-account-left-side"
                            direction="column"
                            justify="flex-start"
                            align="flex-start"
                            row-gap="0"
                        >
                            <VJoyText preset="secondary-800">
                                {{ t('profile.signup.step.account.title') }}
                            </VJoyText>
                            <VJoyWrapper
                                v-if="!isMaltOpen"
                                class="freelancer-signup-account-left-side__social-wrapper"
                                align="stretch"
                                justify="center"
                                row-gap="1"
                                no-margin
                            >
                                <div class="freelancer-signup-account-left-side__social-wrapper__social-btn">
                                    <VJoyButton variant="secondary" data-testid="google-signup-button" @click="signupWithGoogle">
                                        <MaltImg
                                            src="/social-networks/signin-google"
                                            :alt="$t('profile.signup.create.account.social.signup.cta.logo.alt')"
                                            height="18"
                                        />
                                        <span>{{ $t('profile.signup.create.account.social.signup.cta.label') }}</span>
                                    </VJoyButton>
                                    <VJoyFormError
                                        :visible="!!socialSigninAccountAlreadyExistsEmail"
                                        data-testid="freelancer-signup-step-create-your-account-form-error-account-already-exists"
                                    >
                                        <AccountAlreadyExistsError :email="socialSigninAccountAlreadyExistsEmail" />
                                    </VJoyFormError>
                                </div>
                                <VJoySeparator :text="$t('profile.signup.step.generic.divider.or')" size="small" />
                            </VJoyWrapper>
                            <VJoyHighlight v-else accent level="info">
                                {{ $t('profile.signup.create.account.social.signup.open.email.info') }}
                            </VJoyHighlight>
                            <VJoyWrapper item-stretch no-margin class="account-creation-form__email">
                                <VJoyInput
                                    v-model.trim="emailR$.$value.email"
                                    data-testid="account-creation-form-email"
                                    type="text"
                                    name="email"
                                    :placeholder="$t('profile.features.account.form.email.label')"
                                    :disabled="isMaltOpen"
                                    :invalid="emailR$.email.$error"
                                />
                                <VJoyFormError
                                    v-if="emailR$.email.$error"
                                    :errors="emailR$.email.$errors"
                                    class="freelancer-signup-account-left-side__email-error"
                                    data-testid="account-creation-form-email-error"
                                >
                                    <template v-if="isEmailAlreadyUsedError" #default>
                                        <AccountAlreadyExistsError :email="emailForm.email" />
                                    </template>
                                </VJoyFormError>
                            </VJoyWrapper>
                        </VJoyWrapper>
                        <VJoyWrapper class="freelancer-signup-account-right-side" stretch>
                            <VJoyPanel class="freelancer-signup-account-right-side__panel">
                                <template #panel-title>
                                    <VJoyText preset="secondary-200">
                                        {{ $t('profile.signup.create.account.content.title') }}
                                    </VJoyText>
                                </template>
                                <template #panel-body>
                                    <VJoyWrapper
                                        direction="column"
                                        align="flex-start"
                                        class="freelancer-signup-account-right-side__wrapper-panel-body"
                                    >
                                        <VJoyWrapper direction="row" wrap="nowrap" justify="flex-start" align="flex-start" no-margin>
                                            <VJoyIcon
                                                class="freelancer-signup-account-right-side__wrapper-panel-body__icon"
                                                name="check-circle"
                                                color="brand-primary"
                                            />
                                            <p>{{ $t('profile.signup.create.account.content.item1') }}</p>
                                        </VJoyWrapper>
                                        <VJoyWrapper direction="row" wrap="nowrap" justify="flex-start" align="flex-start" no-margin>
                                            <VJoyIcon
                                                class="freelancer-signup-account-right-side__wrapper-panel-body__icon"
                                                name="check-circle"
                                                color="brand-primary"
                                            />
                                            <p>{{ $t('profile.signup.create.account.content.item2') }}</p>
                                        </VJoyWrapper>
                                        <VJoyWrapper direction="row" wrap="nowrap" justify="flex-start" align="flex-start" no-margin>
                                            <VJoyIcon
                                                class="freelancer-signup-account-right-side__wrapper-panel-body__icon"
                                                name="check-circle"
                                                color="brand-primary"
                                            />
                                            <p>{{ $t('profile.signup.create.account.content.item3') }}</p>
                                        </VJoyWrapper>
                                    </VJoyWrapper>
                                </template>
                                <template #panel-action>
                                    <div class="freelancer-signup-account-right-side__shapes">
                                        <div class="freelancer-signup-account-right-side__shapes__wrapper">
                                            <div class="freelancer-signup-account-right-side__shapes__wrapper__shape_1"></div>
                                            <div class="freelancer-signup-account-right-side__shapes__wrapper__shape_2"></div>
                                            <div class="freelancer-signup-account-right-side__shapes__wrapper__shape_3"></div>
                                        </div>
                                    </div>
                                </template>
                            </VJoyPanel>
                        </VJoyWrapper>
                    </VJoyWrapper>
                </template>
                <template #freelancer-signup-multiple-phase-2>
                    <VJoyWrapper title-size="large">
                        <VJoyText preset="secondary-200">
                            {{ $t('profile.signup.step.account.title2') }}
                        </VJoyText>
                        <AccountPasswordCreationForm ref="accountPasswordFormRef" v-model:external-errors="externalErrors" />
                    </VJoyWrapper>
                </template>
            </FreelancerSignupMultiplePhases>
        </template>
    </FreelancerSignupStep>
</template>
<script setup lang="ts">
    import {computed, onMounted, reactive, ref, useTemplateRef, watch} from 'vue';
    import type {RegleExternalErrorTree} from '@regle/core';
    import {email as emailRule, required} from '@regle/rules';
    import {definePageMeta, navigateTo, useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useRouter, useTranslation} from '#imports';
    import {useRegle} from '@malt/forms';
    import {VJoyButton, VJoyFormError, VJoyHighlight, VJoyIcon, VJoyInput, VJoyPanel, VJoySeparator, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import FreelancerSignupMultiplePhases from '~/components/ui/FreelancerSignupMultiplePhases.vue';
    import type {AccountPasswordCreationFormData} from '~/features/account/types/AccountCreationFormData';
    import AccountAlreadyExistsError from '~/features/account/ui/AccountAlreadyExistsError.vue';
    import AccountPasswordCreationForm from '~/features/account/ui/AccountPasswordCreationForm.vue';
    import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
    import {useFreelancerSignupNavigation} from '~/features/freelancer-signup/navigation/composables/useFreelancerSignupNavigation';
    import {useFreelancerSignupNavigationStore} from '~/features/freelancer-signup/navigation/store/FreelancerSignupNavigationStore';
    import {FreelancerSignupStepName} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
    import FreelancerSignupStep from '~/features/freelancer-signup/steps/ui/FreelancerSignupStep.vue';
    import {useFreelancerSignupTracking} from '~/features/freelancer-signup/track/composables/useFreelancerSignupTracking';
    import {
        FreelancerSignupTrackingActionContextStepSaved,
        FreelancerSignupTrackingStepName,
    } from '~/features/freelancer-signup/track/types/FreelancerSignupTracking';
    import {useFreelancerSignupFunctionalError} from '~/features/freelancer-signup/workflow/composables/useFreelancerSignupFunctionalError';
    import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
    import {FreelancerSignupFunctionalErrorCodeEnum} from '~/openapi';

    definePageMeta({
        funnelStepName: FreelancerSignupStepName.CREATE_YOUR_ACCOUNT,
    });

    // Composables
    const trackingStepSavedActionContext = FreelancerSignupTrackingActionContextStepSaved.ACCOUNT_CREATION_WITH_EMAIL;
    const {isErrorWithFunctionalErrorCode, getWorkflowFunctionalErrorCode} = useFreelancerSignupFunctionalError();
    const route = useRoute();
    const router = useRouter();
    const {stepViewed} = useFreelancerSignupTracking();
    const {t} = useTranslation();

    // Stores
    const freelancerSignupStore = useFreelancerSignupStore();
    const {repatriationMetadata} = storeToRefs(freelancerSignupStore);
    const {funnelContext} = storeToRefs(useFreelancerSignupNavigationStore());
    const freelancerSignupNavigation = useFreelancerSignupNavigation();

    // refs
    const accountPasswordFormRef = useTemplateRef<{validate: () => Promise<{valid: boolean; data: AccountPasswordCreationFormData}>}>(
        'accountPasswordFormRef',
    );
    const emailForMaltOpen = computed(() =>
        funnelContext.value === FreelancerSignupContext.OPEN ? repatriationMetadata.value?.freelancerEmail : undefined,
    );
    const socialSigninAccountAlreadyExistsEmail = ref('');
    const displayEmailPhase = ref(true);

    const emailForm = reactive({email: ''});
    const emailExternalErrors = ref<RegleExternalErrorTree<typeof emailForm>>({});
    const externalErrors = ref<RegleExternalErrorTree<AccountPasswordCreationFormData>>({});

    const {r$: emailR$} = useRegle(
        emailForm,
        {
            email: {required, email: emailRule},
        },
        {externalErrors: emailExternalErrors, autoDirty: false},
    );

    // Computed values
    const isEmailAlreadyUsedError = computed(() =>
        emailR$.email.$errors.includes(t('profile.features.account.form.email.error.account-already-exists')),
    );
    const isMaltOpen = computed<boolean>(() => !!emailForMaltOpen.value);

    watch(
        emailForMaltOpen,
        (email) => {
            if (email) {
                emailForm.email = email;
            }
        },
        {immediate: true},
    );

    onMounted(() => {
        const {redirect, pageSource, 'account-already-exists': accountAlreadyExistsParam, ...otherParams} = route.query;
        socialSigninAccountAlreadyExistsEmail.value = typeof accountAlreadyExistsParam === 'string' ? accountAlreadyExistsParam : '';
        router.replace({query: otherParams});
    });

    async function handleVerifyEmail(): Promise<boolean> {
        const {valid} = await emailR$.$validate();

        if (!valid) {
            return false;
        }

        try {
            await freelancerSignupStore.verifyEmail(emailForm.email);
            displayEmailPhase.value = false;
            stepViewed(FreelancerSignupTrackingStepName.ACCOUNT_CREATION);
            return false;
        } catch (error: unknown) {
            if (!isErrorWithFunctionalErrorCode(error)) {
                throw error;
            }

            switch (getWorkflowFunctionalErrorCode(error)) {
                case FreelancerSignupFunctionalErrorCodeEnum.AccountAlreadyExistsForEmail:
                    emailExternalErrors.value = {email: [t('profile.features.account.form.email.error.account-already-exists')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.EmailDomainNotAuthorized:
                    emailExternalErrors.value = {email: [t('profile.signup.create.account.error.email.not.authorized')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.WorkflowAlreadyExistForEmail:
                    await navigateTo({
                        path: '/profile/freelancer-signup/welcome-back',
                        query: {email: emailForm.email},
                    });
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.UserShouldBeLoggedIn:
                    await freelancerSignupNavigation.goToSigninWithRedirectionToFunnel();
                    break;
                default:
                    throw error;
            }
            return false;
        }
    }

    async function handleSaveStep(): Promise<boolean> {
        if (displayEmailPhase.value) {
            return await handleVerifyEmail();
        }

        const result = await accountPasswordFormRef.value?.validate();
        if (!result?.valid) {
            return false;
        }
        const data = result.data;

        try {
            await freelancerSignupStore.saveAccount({
                firstname: data.firstname!,
                lastname: data.lastname!,
                email: emailForm.email,
                password: data.password!,
                isCheckedMaltsTermsAndConditions: data.isCheckedMaltsTermsAndConditions,
                isCheckedMaltsPrivacyPolicy: data.isCheckedMaltsPrivacyPolicy,
            });
            return true;
        } catch (error: unknown) {
            if (!isErrorWithFunctionalErrorCode(error)) {
                throw error;
            }

            switch (getWorkflowFunctionalErrorCode(error)) {
                case FreelancerSignupFunctionalErrorCodeEnum.AccountAlreadyExistsForEmail:
                    emailExternalErrors.value = {email: [t('profile.features.account.form.email.error.account-already-exists')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.EmailDomainNotAuthorized:
                    emailExternalErrors.value = {email: [t('profile.signup.create.account.error.email.not.authorized')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.InvalidFirstname:
                    externalErrors.value = {firstname: [t('validation.message.username')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.InvalidLastname:
                    externalErrors.value = {lastname: [t('validation.message.username')]};
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.WorkflowAlreadyExistForEmail:
                    await navigateTo({
                        path: '/profile/freelancer-signup/welcome-back',
                        query: {email: emailForm.email},
                    });
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.UserShouldBeLoggedIn:
                    await freelancerSignupNavigation.goToSigninWithRedirectionToFunnel();
                    break;
                default:
                    throw error;
            }

            return false;
        }
    }

    function signupWithGoogle() {
        const targetUrl =
            `/registration/oauth2/authorization/google` +
            '?actionType=signup' +
            '&identityType=freelancer' +
            `&errRedirect=${encodeURIComponent('/profile/freelancer-signup/create-your-account/')}` +
            `&redirect=${encodeURIComponent('/profile/freelancer-signup/complete-your-account')}` +
            `&pageSource=freelancer-signup`;

        return navigateTo(targetUrl, {external: true});
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .freelancer-signup-account {
        &-left-side {
            width: 48%;

            @include mq.screen_xs {
                width: 100%;
            }

            &__social-wrapper {
                @include mq.screen_sm {
                    width: 100%;
                }

                &__social-btn {
                    padding-top: var(--joy-core-spacing-5);
                    width: 100%;

                    > .joy-button {
                        width: 100%;

                        .joy-button--slot {
                            display: flex;
                            align-items: center;
                            gap: var(--joy-core-spacing-2);
                        }
                    }

                    > .joy-form-error.joy-has-error {
                        margin-top: var(--joy-core-spacing-2);
                    }
                }
            }

            &__social-btn {
                padding-top: var(--joy-core-spacing-5);
                width: 100%;

                > .joy-button {
                    width: 100%;

                    .joy-button--slot {
                        display: flex;
                        align-items: center;
                        gap: var(--joy-core-spacing-2);
                    }
                }

                > .joy-form-error.joy-has-error {
                    margin-top: var(--joy-core-spacing-2);
                }
            }

            &__email-error {
                width: 100%;
            }
        }

        &-right-side {
            width: 48%;

            @include mq.screen_xs {
                width: 100%;
            }

            &__panel {
                padding: var(--joy-core-spacing-8) !important;
                height: 403px;
                border-radius: var(--joy-core-radius-5) !important;
                overflow: hidden;
                width: 100% !important;
            }

            &__wrapper-panel-body {
                font-size: 18px;
                padding-top: var(--joy-core-spacing-6);
                border-radius: 50px;

                &__icon {
                    margin-top: var(--joy-core-spacing-1);
                }
            }

            &__shapes {
                height: 195px;
                width: 305px;
                overflow: hidden;
                position: absolute;
                bottom: 0;
                right: 0;

                &__wrapper {
                    width: 355px;
                    height: 358px;

                    &__shape_1,
                    &__shape_2,
                    &__shape_3,
                    &__shape_4 {
                        position: absolute;
                        border-radius: 387px;
                    }

                    &__shape_1 {
                        top: 113px;
                        left: 69px;
                        width: 150px;
                        height: 300px;
                        background-color: var(--joy-color-brand-primary-30);
                        transform: rotate(55deg);
                    }

                    &__shape_2 {
                        top: 89px;
                        left: 180px;
                        width: 170px;
                        height: 271px;
                        background-color: var(--joy-color-primary-30);
                        transform: rotate(32deg);
                    }

                    &__shape_3 {
                        top: 10px;
                        left: 221px;
                        width: 150px;
                        height: 485px;
                        background-color: var(--joy-color-primary-50);
                        transform: rotate(17deg);
                    }

                    &__shape_4 {
                        top: 34px;
                        left: 290px;
                        width: 83px;
                        height: 321px;
                        background-color: var(--joy-color-tertiary-70);
                        transform: rotate(-9deg);
                    }
                }
            }
        }
    }

    .freelancer-signup-multiple-phases__phase2 {
        margin: 0;
    }
</style>
